import { useFormikContext } from 'formik';
import React from 'react';
import _ from 'underscore';

import { DictionaryType } from '~commonInfra/utils/DictionaryType';

import { RegisterCallbackAction, SettingsCoordinatorAction, UpdateFormStateAction } from './SettingsCoordinatorActions';
import { SectionNameContext, SettingsCoordinatorContext } from './SettingsCoordinatorContexts';

export type GeneralSettingsCoordinatorState = {
  saveRevertCallbacks: DictionaryType<Pick<RegisterCallbackAction, 'submit' | 'revert' | 'resetDefault'>>;
  subformStates: DictionaryType<Pick<UpdateFormStateAction, 'dirty' | 'isValid' | 'isSubmitting'>>;
}

const settingsCoordinatorReducer: React.Reducer<GeneralSettingsCoordinatorState, SettingsCoordinatorAction> =
  (prevState, action) => {
    switch (action.type) {
      case 'REGISTER_CALLBACKS':
        const prevSaveRevert = prevState.saveRevertCallbacks[action.formName];

        if (prevSaveRevert?.submit === action.submit
          && prevSaveRevert?.revert === action.revert
          && prevSaveRevert?.resetDefault === action.resetDefault) {
          return prevState;
        }

        return {
          ...prevState,
          saveRevertCallbacks: {
            ...prevState.saveRevertCallbacks,
            [action.formName]: {
              submit: action.submit,
              revert: action.revert,
              resetDefault: action.resetDefault
            },
          },
        };

      case 'UNREGISTER_CALLBACKS': {
        const prevSaveRevert = prevState.saveRevertCallbacks[action.formName];

        if (!prevSaveRevert) {
          return prevState;
        }

        const updatedCallbacks = {...prevState.saveRevertCallbacks};
        delete updatedCallbacks[action.formName];

        const updatedStates = {...prevState.subformStates};
        delete updatedStates[action.formName];

        return {
          ...prevState,
          saveRevertCallbacks: updatedCallbacks,
          subformStates: updatedStates
        };
      }

      case 'UPDATE_FORM_STATE':
        const prevFlagState = prevState.subformStates[action.formName];

        if (prevFlagState?.isValid === action.isValid
          && prevFlagState?.dirty === action.dirty
          && prevFlagState?.isSubmitting === action.isSubmitting
        ) {
          return prevState;
        }

        return {
          ...prevState,
          subformStates: {
            ...prevState.subformStates,
            [action.formName]: {
              dirty: action.dirty,
              isValid: action.isValid,
              isSubmitting: action.isSubmitting,
            },
          },
        };

      default:
        return prevState;
    }
  };

export interface SettingsCoordinatorState {
  /** Trigger the submit callback of all registered forms and then call the onSubmit handler */
  triggerSave: () => Promise<void>;
  /** Trigger the reset callback of all registered forms and wait for their promises to finish */
  triggerRevert: () => Promise<void>;

  /**
   * Trigger reset to default values callback and wait for promises to finish
   */
  triggerResetDefault?: () => Promise<void>;
  /** Is any registered form dirty */
  isAnyDirty: boolean;
  /** Is any registered form invalid */
  isAnyInvalid: boolean;
  /** Is any registered form submitting */
  isAnySubmitting: boolean;
  /**
   * The dispatch function to register forms and update their flags with the settings coordinator. This
   * should be passed down to components via a {@link SettingsCoordinatorContext}
   */
  dispatch: React.Dispatch<SettingsCoordinatorAction>;
}

/**
 * A hook used to create the necessary state and callbacks for a settings coordinator.
 *
 * **IMPORTANT** This is meant to be
 * used by the SettingsCoordinator component. To wire a Formik form under a SettingsCoordinator, see the
 * {@link useSettingsCoordinator} hook
 * @param onSubmit - A callback after all registered forms are successfully submitted
 * @param onSubmitFailure - A callback after any registered form fails to submit
 * @param onResetToDefaultSuccess - A callback after all registered forms successfully reset
 * @param onResetToDefaultFailure - A callback after any registered form fails to reset
 */
export function useSettingsCoordinatorStateInternal(
  onSubmit?: () => Promise<unknown>,
  onSubmitFailure?: (error: any) => Promise<void>,
  onResetToDefaultSuccess?: () => Promise<void>,
  onResetToDefaultFailure?: (error: any) => Promise<void>,
  submitSequentially: boolean = false
): SettingsCoordinatorState {

  // create a settings coordinator reducer. The state will be maintained internally and is exposed through
  // some triggers and calculated flags. The dispatch function is also returned and should be passed
  // down to components via a SettingsCoordinatorContext
  const [state, dispatch] = React.useReducer(settingsCoordinatorReducer, {
    saveRevertCallbacks: {},
    subformStates: {},
  });

  const triggerSave = React.useCallback(async () => {
    try {
      if(submitSequentially){
        const callbacks = Object.values(state.saveRevertCallbacks);
        for(let i = 0; i<callbacks.length; i++){
          await callbacks[i].submit();
        }
      } else{
        await Promise.all(Object.values(state.saveRevertCallbacks).map(callbacks => callbacks.submit()));
      }
      await onSubmit?.();
    } catch (error) {
      await onSubmitFailure?.(error);
    }
  }, [state?.saveRevertCallbacks, onSubmit, onSubmitFailure, submitSequentially]);

  const triggerRevert = React.useCallback(async () => {
    await Promise.all(Object.values(state.saveRevertCallbacks).map(callbacks => callbacks.revert()));
  }, [state?.saveRevertCallbacks]);

  const triggerResetDefault = React.useCallback(async () => {
    try {
      await Promise.all(Object.values(state.saveRevertCallbacks).map(callbacks => callbacks.resetDefault?.()));
      await onResetToDefaultSuccess?.();
      await triggerRevert();
    } catch (error) {
      await onResetToDefaultFailure?.(error);
    }
  }, [state?.saveRevertCallbacks, onResetToDefaultFailure, onResetToDefaultSuccess, triggerRevert]);

  const isAnyDirty = React.useMemo(() => Object.values(state.subformStates).some(form => form.dirty)
    , [state?.subformStates]);

  const isAnyInvalid = React.useMemo(() => Object.values(state.subformStates).some(form => !form.isValid)
    , [state?.subformStates]);

  const isAnySubmitting = React.useMemo(() => Object.values(state.subformStates).some(form => form.isSubmitting)
    , [state?.subformStates]);

  return {
    triggerSave,
    triggerRevert,
    triggerResetDefault,
    isAnyDirty,
    isAnyInvalid,
    isAnySubmitting,
    dispatch,
  };
}

/**
 * A hook to wire the current formik form to the settings coordinator. It must be used in a component
 * nested within Formik, which in turn is nested under settings coordinator
 * @param formName - an optional form name to register with. It can be omitted if used within a section list
 * @param resetToDefaultHandler - an optional handler for resetting the form to server defaults
 */
export function useSettingsCoordinator<T extends object>(formName?: string, resetToDefaultHandler?: () => Promise<any>) {
  const subform = useFormikContext();
  const dispatch = React.useContext(SettingsCoordinatorContext);
  if (!subform || !dispatch) {
    throw new Error(`useSettingsCoordinator() must be called in a component nested under Formik component, which
in turn is nested under a SettingsCoordinator component`);
  }

  const sectionName = React.useContext(SectionNameContext);

  // Formik issues two consecutive updates for the dirty and isValid flags, which causes flickering
  // Create a debounced version of dispatch to work around that
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedDispatch = React.useCallback(_.debounce(dispatch, 50), [dispatch]);

  React.useEffect(() => {
    dispatch?.({
      type: 'REGISTER_CALLBACKS',
      formName: formName ?? sectionName,
      submit: subform.submitForm,
      revert: subform.resetForm,
      resetDefault: resetToDefaultHandler
    });

    return () => {
      dispatch?.({
        type: 'UNREGISTER_CALLBACKS',
        formName: formName ?? sectionName,
      });
    };
  }, [subform?.submitForm, subform?.resetForm, dispatch, sectionName, formName, resetToDefaultHandler]);

  React.useEffect(() => {
    debouncedDispatch?.({
      type: 'UPDATE_FORM_STATE',
      formName: formName ?? sectionName,
      isValid: subform.isValid,
      dirty: subform.dirty,
      isSubmitting: subform.isSubmitting,
    });
  }, [subform?.dirty, subform?.isValid, subform?.isSubmitting, debouncedDispatch, sectionName, formName]);

}
